/**
 * ResellersByStandingStrip (TMMPDM-0022): dashboard strip of standing-state chips with counts,
 * each linking to /resellers; DEACTIVATION_DUE count > 0 shows an error Alert.
 */
import { Alert, Link, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import type { ResellerStateCount, ResellerStandingState } from '@/api/tmm/types';
import { StatusChip } from '@/status';

const STATE_FALLBACK: Record<ResellerStandingState, string> = {
  GOOD: 'Good',
  PROBATION: 'Probation',
  DEACTIVATION_DUE: 'Deactivation due',
  DEACTIVATED: 'Deactivated',
};

interface ResellersByStandingStripProps {
  counts: ResellerStateCount[];
}

export function ResellersByStandingStrip({ counts }: ResellersByStandingStripProps) {
  const { t } = useCopy();
  if (counts.length === 0) return null;

  const due = counts.find((row) => row.state === 'DEACTIVATION_DUE');
  const dueCount = due?.count ?? 0;

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      {dueCount > 0 ? (
        <Alert severity="error">
          <Link component={RouterLink} to="/partners" color="inherit" underline="always">
            {t(
              'dashboard.standing.deactivationDue',
              '{{count}} resellers missed their minimum through probation and are due for deactivation.',
              { count: dueCount },
            )}
          </Link>
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {counts.map((row) => (
          <StatusChip
            key={row.state}
            component={RouterLink}
            to="/partners"
            clickable
            status={row.state}
            label={`${t(`resellers.standing.${row.state}`, STATE_FALLBACK[row.state])}: ${row.count}`}
          />
        ))}
      </Stack>
    </Stack>
  );
}
